import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { StickyNote, X, Send } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function FloatingNote({ api, paperId, currentPage, onNoteSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleSave = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${api}/notes`, {
        paper_id: paperId,
        page_number: currentPage,
        text: text.trim(),
      }, { withCredentials: true });
      if (onNoteSaved) onNoteSaved(res.data);
      toast.success("Note saved!");
      setText("");
      setOpen(false);
    } catch {
      toast.error("Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      handleSave();
    }
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-20 z-50" data-testid="floating-note">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="mb-3 w-72"
          >
            <div className="bg-white dark:bg-[#1E1E1E] border-2 border-neo-border dark:border-[#444] shadow-[4px_4px_0px_0px_#1A1A1A] dark:shadow-[4px_4px_0px_0px_#444] rounded-xl p-3" data-testid="floating-note-input">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-heading font-bold dark:text-white">
                  Note — Page {currentPage + 1}
                </span>
                <button onClick={() => setOpen(false)} className="text-neo-text-secondary dark:text-gray-400 hover:text-neo-red">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <textarea
                ref={inputRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your note... (Ctrl+Enter to save)"
                className="w-full border-2 border-neo-border/30 dark:border-[#444]/60 dark:bg-[#2A2A2A] dark:text-white rounded-lg p-2 text-sm resize-none focus:outline-none focus:border-neo-yellow min-h-[80px]"
                rows={3}
                data-testid="floating-note-textarea"
              />
              <button
                onClick={handleSave}
                disabled={saving || !text.trim()}
                className={`mt-2 neo-btn w-full text-xs py-1.5 flex items-center justify-center gap-1 ${
                  saving || !text.trim() ? "bg-gray-200 text-gray-500 border-gray-300 shadow-none cursor-not-allowed" : "neo-btn-primary"
                }`}
                data-testid="floating-note-save"
              >
                <Send className="w-3 h-3" />
                {saving ? "Saving..." : "Save Note"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button */}
      <motion.button
        onClick={() => setOpen(!open)}
        className="w-12 h-12 rounded-full bg-neo-yellow border-2 border-neo-border shadow-[3px_3px_0px_0px_#1A1A1A] flex items-center justify-center hover:shadow-[1px_1px_0px_0px_#1A1A1A] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="floating-note-btn"
      >
        {open ? <X className="w-5 h-5 text-neo-text" /> : <StickyNote className="w-5 h-5 text-neo-text" />}
      </motion.button>
    </div>
  );
}
